import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useRef,
} from "react";
import {
  useGetOrganizationsQuery,
  useGetCoursesByIdQuery,
  useGetCoursesByCourseIdChaptersQuery,
} from "../store/api/umbraco-api";

interface Course {
  id: number;
  name: string;
}

interface CoursesContextType {
  courses: Course[];
  selectedItem: Course | null;
  setSelectedItem: (course: Course | null) => void;
  chapters: any[];
  isLoading: boolean;
  isChaptersLoading: boolean;
  completedChaptersCount: number;
  inProgressChaptersCount: number;
  selectedCourse: Course | null;
  setSelectedCourse: (course: Course | null) => void;
  selectedCourseData: any | null;
}

const CoursesContext = createContext<CoursesContextType | undefined>(undefined);

export function CoursesProvider({ children }: { children: ReactNode }) {
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedItem, setSelectedItem] = useState<Course | null>(null);
  const { data, isLoading: isOrganizationsLoading } =
    useGetOrganizationsQuery();
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);

  // Fetch selected course data
  const { data: selectedCourseData, isLoading: isCourseLoading } =
    useGetCoursesByIdQuery(
      { id: selectedCourse?.id || 0 },
      { skip: !selectedCourse?.id }
    );

  // Fetch chapters for selected course
  const { data: chapters = [], isLoading: isChaptersLoading } =
    useGetCoursesByCourseIdChaptersQuery(
      { courseId: selectedCourse?.id || 0 },
      { skip: !selectedCourse?.id }
    );

  // Calculate chapter statistics
  const completedChaptersCount = chapters.filter(
    (chapter) =>
      (chapter.completed || 0) === (chapter.total || 0) &&
      (chapter.total || 0) > 0
  ).length;

  const inProgressChaptersCount = chapters.filter(
    (chapter) =>
      (chapter.completed || 0) > 0 &&
      (chapter.completed || 0) < (chapter.total || 0)
  ).length;

  // Use a ref to track if we've already initialized
  const initializedRef = useRef(false);

  // Single effect to handle all course loading and selection logic
  useEffect(() => {

    // Skip if still loading or no data
    if (isOrganizationsLoading || !data) {
      console.log("⏸️ Skipping effect - Loading or no data");
      return;
    }

    // Extract courses from data
    const allCourses = data.flatMap(
      (org) =>
        org.courses
          ?.map((course) => ({
            id: course.id,
            name: course.name,
          }))
          .filter(
            (course): course is Course =>
              course.id !== undefined && course.name !== undefined
          ) || []
    );

    console.log("📋 Extracted Courses:", {
      totalCourses: allCourses.length,
      courseIds: allCourses.map((c) => c.id),
      selectedCourseId: selectedCourse?.id,
      selectedItemId: selectedItem?.id,
    });

    // Update courses state
    setCourses(allCourses);

    // Only proceed with selection logic if we have courses
    if (allCourses.length === 0) {
      console.log("⚠️ No courses available");
      return;
    }

    // Case 1: We have a selected course in Redux but not in context
    if (
      selectedCourse?.id &&
      (!selectedItem || selectedItem.id !== selectedCourse.id)
    ) {
      console.log("🔄 Syncing from Redux to Context:", {
        selectedCourseId: selectedCourse.id,
        selectedCourseName: selectedCourse.name,
        currentSelectedItem: selectedItem,
      });

      // Find the course in allCourses to ensure we have the correct data
      const courseToSelect = allCourses.find((c) => c.id === selectedCourse.id);
      if (courseToSelect) {
        setSelectedItem(courseToSelect);
      } else {
        console.log("⚠️ Selected course not found in available courses:", {
          selectedCourseId: selectedCourse.id,
          availableCourseIds: allCourses.map((c) => c.id),
        });
      }
      return;
    }

    // Case 2: We have a selected item in context but not in Redux
    if (
      selectedItem &&
      (!selectedCourse?.id || selectedCourse?.id !== selectedItem.id)
    ) {
      return;
    }

    // Case 3: No selection anywhere, select first course
    if (!selectedItem && !selectedCourse?.id) {
      const firstCourse = allCourses[0];
      setSelectedItem(firstCourse);
      setSelectedCourse(firstCourse);
      initializedRef.current = true;
    }
  }, [data, isOrganizationsLoading, selectedCourse, selectedItem]);

  // Add a separate effect to handle initial course selection
  useEffect(() => {
    if (selectedCourse?.id && !selectedItem && courses.length > 0) {
      console.log("🔄 Initial Course Selection:", {
        selectedCourseId: selectedCourse.id,
        coursesAvailable: courses.length,
      });
      const courseToSelect = courses.find((c) => c.id === selectedCourse.id);
      if (courseToSelect) {
        setSelectedItem(courseToSelect);
      }
    }
  }, [selectedCourse?.id, selectedItem, courses]);

  const value = {
    courses,
    selectedItem,
    setSelectedItem,
    chapters,
    isLoading: isCourseLoading || isOrganizationsLoading,
    isChaptersLoading,
    completedChaptersCount,
    inProgressChaptersCount,
    selectedCourse,
    setSelectedCourse,
    selectedCourseData
  };

  return (
    <CoursesContext.Provider value={value}>{children}</CoursesContext.Provider>
  );
}

export function useCourses() {
  const context = useContext(CoursesContext);
  if (!context) {
    throw new Error("יש להשתמש ב-useCourses בתוך CoursesProvider");
  }
  return context;
}

export default CoursesContext;
